import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Card,
  CardBody,
  Heading,
  useToast,
  Select,
  Text,
  Image,
  Badge,
  Divider,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Grid,
} from '@chakra-ui/react';
import { FiEye, FiUpload, FiMusic, FiX } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiService } from '../services/apiService';
import { useBlogCategories } from '../hooks/useBlogCategories';
import { nettoyerHtml } from '../utils/nettoyerHtml';

const API = process.env.REACT_APP_API_URL || '/api';
const getImgUrl = (p) => !p ? null : p.startsWith('http') ? p : `${API.replace('/api', '')}${p.startsWith('/') ? p : '/' + p}`;

export default function BlogEditor({ article = null, onSave, onCancel }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();

  const { categories } = useBlogCategories();

  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    resume: '',
    categorie: '',
    is_published: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [audioFileName, setAudioFileName] = useState('');

  // Valeur par défaut de la catégorie une fois la liste chargée (nouvel article uniquement)
  useEffect(() => {
    if (!article && !formData.categorie && categories.length > 0) {
      setFormData(prev => ({ ...prev, categorie: categories[0].valeur }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {
    if (article) {
      setFormData({
        titre: article.titre || '',
        contenu: article.contenu || '',
        resume: article.resume || '',
        categorie: article.categorie || '',
        is_published: !!article.is_published,
      });
      setImagePreview(article.image_principale ? getImgUrl(article.image_principale) : '');
      setImageFile(null);
      setAudioPreview(article.audio_url ? getImgUrl(article.audio_url) : '');
      setAudioFile(null);
      setAudioFileName('');
    }
  }, [article]);

  // Configuration de l'éditeur Quill
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Erreur', description: 'Veuillez sélectionner une image', status: 'error', duration: 3000 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Erreur', description: 'L\'image ne doit pas dépasser 5MB', status: 'error', duration: 3000 });
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      toast({ title: 'Erreur', description: 'Veuillez sélectionner un fichier audio (mp3, wav, ogg, m4a...)', status: 'error', duration: 3000 });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: 'Erreur', description: 'Le fichier audio ne doit pas dépasser 20MB', status: 'error', duration: 3000 });
      return;
    }

    setAudioFile(file);
    setAudioFileName(file.name);
    setAudioPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!formData.titre || !formData.contenu) {
      toast({
        title: 'Erreur',
        description: 'Le titre et le contenu sont obligatoires',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const endpoint = article ? `/blog/admin/posts/${article.id}` : '/blog/admin/posts';
      const method = article ? 'PUT' : 'POST';

      const body = new FormData();
      body.append('titre', formData.titre);
      body.append('contenu', formData.contenu);
      body.append('resume', formData.resume || '');
      body.append('categorie', formData.categorie);
      body.append('is_published', formData.is_published ? 'true' : 'false');
      if (imageFile) body.append('image_principale', imageFile);
      if (audioFile) body.append('audio', audioFile);

      const data = await apiService.makeRequest(endpoint, { method, body });

      toast({
        title: 'Succès',
        description: data.message || (article ? 'Article mis à jour' : 'Article créé'),
        status: 'success',
        duration: 4000,
      });

      if (onSave) onSave();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderArticlePreview = () => {
    return (
      <Box>
        {imagePreview && (
          <Image
            src={imagePreview}
            alt={formData.titre}
            width="100%"
            maxH="400px"
            objectFit="cover"
            borderRadius="lg"
            mb={6}
          />
        )}

        <Heading size="xl" mb={2}>
          {formData.titre || 'Titre de l\'article'}
        </Heading>

        {formData.resume && (
          <Text color="gray.600" mb={4} fontStyle="italic">
            {formData.resume}
          </Text>
        )}

        <HStack mb={4}>
          <Badge colorScheme="green">
            {categories.find(c => c.valeur === formData.categorie)?.libelle || formData.categorie}
          </Badge>
          <Badge colorScheme={formData.is_published ? 'blue' : 'yellow'}>
            {formData.is_published ? 'Publié' : 'Brouillon'}
          </Badge>
        </HStack>

        {audioPreview && (
          <Box mb={4}>
            <audio controls src={audioPreview} style={{ width: '100%' }} />
          </Box>
        )}

        <Divider my={4} />

        <Box
          className="ql-editor"
          dangerouslySetInnerHTML={{ __html: nettoyerHtml(formData.contenu) }}
          sx={{
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 'md',
              my: 4
            },
            '& a': {
              color: 'blue.500',
              textDecoration: 'underline'
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'gray.300',
              pl: 4,
              py: 2,
              fontStyle: 'italic',
              color: 'gray.700'
            }
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Tabs colorScheme="green" variant="enclosed">
        <TabList>
          <Tab>✏️ Édition</Tab>
          <Tab>👁️ Aperçu</Tab>
        </TabList>

        <TabPanels>
          {/* Onglet Édition */}
          <TabPanel p={0} pt={6}>
            <VStack spacing={6} align="stretch">
              {/* Informations générales */}
              <Card>
                <CardBody>
                  <VStack spacing={4}>
                    <Heading size="md">
                      {article ? 'Modifier l\'article' : 'Nouvel article'}
                    </Heading>

                    <FormControl isRequired>
                      <FormLabel>Titre</FormLabel>
                      <Input
                        value={formData.titre}
                        onChange={(e) => handleChange('titre', e.target.value)}
                        placeholder="Titre de l'article"
                        size="lg"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Résumé</FormLabel>
                      <Textarea
                        value={formData.resume}
                        onChange={(e) => handleChange('resume', e.target.value)}
                        placeholder="Court résumé affiché dans la liste des articles"
                        rows={2}
                      />
                    </FormControl>

                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="full">
                      <FormControl>
                        <FormLabel>Catégorie</FormLabel>
                        <Select
                          value={formData.categorie}
                          onChange={(e) => handleChange('categorie', e.target.value)}
                        >
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.valeur}>{cat.libelle}</option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb={0}>Publié</FormLabel>
                        <Switch
                          isChecked={formData.is_published}
                          onChange={(e) => handleChange('is_published', e.target.checked)}
                          colorScheme="green"
                        />
                      </FormControl>
                    </Grid>
                  </VStack>
                </CardBody>
              </Card>

              {/* Image de couverture */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md">Image de couverture</Heading>

                    <Button
                      leftIcon={<FiUpload />}
                      as="label"
                      htmlFor="image-upload"
                      cursor="pointer"
                      alignSelf="start"
                    >
                      {imageFile ? imageFile.name : 'Télécharger une image'}
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                      />
                    </Button>

                    {imagePreview && (
                      <Box>
                        <Image
                          src={imagePreview}
                          alt="Aperçu"
                          maxH="300px"
                          width="100%"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Fichier audio */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md">Fichier audio (optionnel)</Heading>
                    <Text fontSize="sm" color="gray.500">
                      Prédication, témoignage ou tout autre enregistrement à joindre à l'article (mp3, wav, ogg, m4a — 20MB max).
                    </Text>

                    <HStack>
                      <Button
                        leftIcon={<FiMusic />}
                        as="label"
                        htmlFor="audio-upload"
                        cursor="pointer"
                        alignSelf="start"
                      >
                        {audioFileName || (audioPreview ? 'Remplacer le fichier audio' : 'Télécharger un fichier audio')}
                        <input
                          id="audio-upload"
                          type="file"
                          accept="audio/*"
                          onChange={handleAudioUpload}
                          style={{ display: 'none' }}
                        />
                      </Button>
                      {audioFile && (
                        <IconButton
                          icon={<FiX />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Annuler la sélection"
                          onClick={() => {
                            setAudioFile(null);
                            setAudioFileName('');
                            setAudioPreview(article?.audio_url ? getImgUrl(article.audio_url) : '');
                          }}
                        />
                      )}
                    </HStack>

                    {audioPreview && (
                      <Box>
                        <audio controls src={audioPreview} style={{ width: '100%' }} />
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Éditeur de contenu */}
              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md">Contenu de l'article</Heading>
                    <Box
                      sx={{
                        '.quill': {
                          bg: 'white',
                          borderRadius: 'md',
                          border: '1px solid',
                          borderColor: 'gray.200',
                        },
                        '.ql-toolbar': {
                          borderTopRadius: 'md',
                          borderBottom: '1px solid',
                          borderColor: 'gray.200',
                          bg: 'gray.50'
                        },
                        '.ql-container': {
                          borderBottomRadius: 'md',
                          minHeight: '400px',
                          fontSize: '16px'
                        }
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={formData.contenu}
                        onChange={(value) => handleChange('contenu', value)}
                        modules={modules}
                        formats={formats}
                        placeholder="Écrivez votre article ici..."
                      />
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              {/* Boutons d'action */}
              <HStack justify="flex-end" spacing={3}>
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  isDisabled={loading}
                >
                  Annuler
                </Button>
                <Button
                  leftIcon={<FiEye />}
                  variant="outline"
                  onClick={onPreviewOpen}
                  isDisabled={loading}
                >
                  Aperçu
                </Button>
                <Button
                  colorScheme="green"
                  onClick={handleSubmit}
                  isLoading={loading}
                  loadingText="Enregistrement..."
                >
                  {article ? 'Mettre à jour' : 'Créer l\'article'}
                </Button>
              </HStack>
            </VStack>
          </TabPanel>

          {/* Onglet Aperçu */}
          <TabPanel>
            <Card>
              <CardBody p={8}>
                {renderArticlePreview()}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal d'aperçu */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Aperçu de l'article</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {renderArticlePreview()}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
